import { useCallback, useEffect, useState } from "react";

const KEY = "progsity:playground:guide-hidden";
const VISIBILITY_EVENT = "progsity:playground:guide-visibility";

export function loadLanguageGuideHidden(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

function persistLanguageGuideHidden(hidden: boolean): void {
  try {
    if (hidden) window.localStorage.setItem(KEY, "1");
    else window.localStorage.removeItem(KEY);
  } catch {
    /* quota / private mode */
  }
}

/**
 * Language-guide visibility below the SEO IDE — shared between the guide's own
 * hide button and the editor-settings toggle. Server render is always visible
 * (SEO content stays in crawlable HTML); the persisted preference applies after
 * hydration. Synced across components via a window event.
 */
export function useLanguageGuideVisibility(): {
  hidden: boolean;
  setHidden: (hidden: boolean) => void;
} {
  const [hidden, setHiddenState] = useState(false);

  useEffect(() => {
    setHiddenState(loadLanguageGuideHidden());
    const sync = () => setHiddenState(loadLanguageGuideHidden());
    window.addEventListener(VISIBILITY_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(VISIBILITY_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const setHidden = useCallback((next: boolean) => {
    setHiddenState(next);
    persistLanguageGuideHidden(next);
    window.dispatchEvent(new Event(VISIBILITY_EVENT));
  }, []);

  return { hidden, setHidden };
}
